/**
 * Canonical ASI brand tokens — light + dark.
 *
 * Values traced from (do not invent new hex here):
 * - src/config/theme.js (primary/secondary)
 * - src/theme/appLightTheme.js (fonts; light palette via muiTheme)
 * - src/theme/appDarkTheme.js (navy/gold scale, dark palette, gold gradients)
 * - src/shared/utils/tokens.js (radius; light page/surface/text named constants)
 *
 * Framework-agnostic: plain objects only. No MUI / Remotion / RN imports.
 */

/** @typedef {'light' | 'dark'} ThemeMode */

export const FONT_BODY =
  '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';
/** Display / heading — Federo (app themes use serif; config/theme.js historically said sans-serif). */
export const FONT_DISPLAY = '"Federo", serif';

export const radius = {
  card: 8,
  button: 6,
  chip: 16,
  dialog: 12,
};

/** Light clinical palette (HealthDesk / MasterChart default). */
export const lightTheme = {
  mode: 'light',
  colors: {
    primary: '#1B4584',
    secondary: '#117ACA',
    /** Page background — tokens.colors.bgPage */
    background: '#F5F8FC',
    /** Card / paper — tokens.colors.bgCard */
    surface: '#FFFFFF',
    textPrimary: '#333333',
    textSecondary: '#666666',
    textDisabled: '#9E9E9E',
    divider: '#E0E0E0',
    error: '#d32f2f',
    warning: '#ed6c02',
    success: '#2e7d32',
    info: '#0288d1',
    /** Aliases used by Remotion / marketing for light-mode navy-scale fills */
    navyDeep: '#1B4584',
    navy: '#117ACA',
    navyElevated: '#FFFFFF',
    navySurface: '#F5F8FC',
    royalMid: '#117ACA',
    goldAntique: '#1B4584',
    goldPolished: '#117ACA',
    goldBrushed: '#153a6d',
    goldMetal: '#1B4584',
    goldMetalText: '#1B4584',
  },
  fonts: {
    display: FONT_DISPLAY,
    body: FONT_BODY,
  },
  radius,
};

/** Dark navy/gold palette (appDarkTheme / VoxPont / FusionMSI dark). */
export const darkTheme = {
  mode: 'dark',
  colors: {
    primary: '#b08a28',
    secondary: '#2D5FA3',
    background: '#0C1E36',
    surface: '#1A3352',
    textPrimary: '#FFFFFF',
    textSecondary: '#C9D6E8',
    textDisabled: 'rgba(255, 255, 255, 0.5)',
    divider: 'rgba(196, 154, 44, 0.28)',
    error: '#FF7A7A',
    warning: '#FFB547',
    success: '#5BD98A',
    info: '#6FB1F5',
    navyDeep: '#0C1E36',
    navy: '#122B4D',
    navyElevated: '#1A3352',
    navySurface: '#1E3A5F',
    royalMid: '#2D5FA3',
    secondaryLight: '#4f7cbb',
    secondaryDark: '#1B4584',
    goldAntique: '#b08a28',
    goldPolished: '#d4af37',
    goldBrushed: '#8c6a1a',
    /** Gradient stop also used in VoxPont marketing CSS */
    goldDeep: '#7a5e17',
    goldMetal:
      'linear-gradient(105deg, #7a5e17 0%, #8c6a1a 16%, #b08a28 34%, #d4af37 50%, #b08a28 66%, #8c6a1a 84%, #7a5e17 100%)',
    goldMetalText:
      'linear-gradient(135deg, #7a5e17 0%, #8c6a1a 15%, #b08a28 32%, #d4af37 50%, #b08a28 68%, #8c6a1a 82%, #7a5e17 100%)',
    goldMetalVertical:
      'linear-gradient(180deg, transparent 0%, #7a5e17 12%, #8c6a1a 28%, #b08a28 44%, #d4af37 56%, #b08a28 70%, #8c6a1a 85%, transparent 100%)',
  },
  fonts: {
    display: FONT_DISPLAY,
    body: FONT_BODY,
  },
  radius,
};

/**
 * @param {ThemeMode} [mode='light']
 * @returns {typeof lightTheme | typeof darkTheme}
 */
export function getTheme(mode = 'light') {
  return mode === 'dark' ? darkTheme : lightTheme;
}
